/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {ChevronDownIcon} from '../../icons'
import {
    Flex,
    Stack,
    Text,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

const toPositiveAmount = (value) => {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? Math.abs(parsedValue) : 0
}

const roundCurrency = (value) => Math.round(value * 100) / 100

const renderSavingsLineLabel = (lineKey) => {
    if (lineKey === 'coupon') {
        return (
            <FormattedMessage
                id="order_summary.savings.promo_code_applied"
                defaultMessage="Promo Code Applied"
            />
        )
    }

    if (lineKey === 'loyalty') {
        return (
            <FormattedMessage
                id="order_summary.savings.collectif_points"
                defaultMessage="Collectif Points Redeemed"
            />
        )
    }

    return (
        <FormattedMessage
            id="order_summary.savings.promo_regular_price"
            defaultMessage="Price Reduction"
        />
    )
}

const buildSavingsBreakdown = ({basket}) => {
    const savings = basket?.c_savings || {}
    const coupons = Array.isArray(savings?.coupons) ? savings.coupons : []
    const loyalty = Array.isArray(savings?.loyalty) ? savings.loyalty : []
    const totalNoCouponDiscount = roundCurrency(toPositiveAmount(savings?.totalNoCouponDiscount))

    const breakdownLines = []

    coupons.forEach((coupon, index) => {
        const couponAmount = roundCurrency(toPositiveAmount(coupon?.amount))
        if (couponAmount > 0) {
            breakdownLines.push({
                key: `coupon_${index}`,
                lineType: 'coupon',
                detail: coupon?.calloutMsg || '',
                codeUsed: coupon?.couponCode || '',
                amount: couponAmount
            })
        }
    })

    loyalty.forEach((loyaltyEntry, index) => {
        const loyaltyAmount = roundCurrency(toPositiveAmount(loyaltyEntry?.amount))
        if (loyaltyAmount > 0) {
            breakdownLines.push({
                key: `loyalty_${index}`,
                lineType: 'loyalty',
                detail:
                    Number(loyaltyEntry?.points) > 0
                        ? `${Number(loyaltyEntry.points)} points applied`
                        : 'Points applied',
                amount: loyaltyAmount
            })
        }
    })

    if (totalNoCouponDiscount > 0) {
        breakdownLines.push({
            key: 'promo_regular_price',
            lineType: 'promo_regular_price',
            detail: '',
            amount: totalNoCouponDiscount
        })
    }

    const derivedTotalSavingsAmount = roundCurrency(
        breakdownLines.reduce((sum, line) => sum + roundCurrency(toPositiveAmount(line.amount)), 0)
    )
    const totalSavingsAmount = roundCurrency(
        toPositiveAmount(savings?.totalOrderDiscount) || derivedTotalSavingsAmount
    )

    return {
        hasSavings: totalSavingsAmount > 0,
        totalSavingsAmount,
        breakdownLines
    }
}

const SavingsAccordion = ({basket}) => {
    const orderSummaryTheme = useMultiStyleConfig('OrderSummary')

    const {hasSavings, totalSavingsAmount, breakdownLines} = buildSavingsBreakdown({basket})

    if (!hasSavings) {
        return null
    }

    return (
        <Accordion allowToggle {...orderSummaryTheme.savingsAccordion}>
            <AccordionItem {...orderSummaryTheme.savingsAccordionItem}>
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            data-testid="order-summary-button-savings-accordion"
                            {...orderSummaryTheme.savingsAccordionButton}
                        >
                            <Flex {...orderSummaryTheme.savingsHeaderContent}>
                                <Flex {...orderSummaryTheme.savingsHeaderLabelWrap}>
                                    <Text {...orderSummaryTheme.rowText}>
                                        <FormattedMessage
                                            defaultMessage="Savings"
                                            id="order_summary.label.savings"
                                        />
                                    </Text>
                                    <ChevronDownIcon
                                        {...orderSummaryTheme.savingsAccordionArrow}
                                        {...(isExpanded &&
                                            orderSummaryTheme.savingsAccordionArrowExpanded)}
                                    />
                                </Flex>
                                <Text {...orderSummaryTheme.rowTextSavings}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={-totalSavingsAmount}
                                    />
                                </Text>
                            </Flex>
                        </AccordionButton>
                        <AccordionPanel {...orderSummaryTheme.savingsAccordionPanel}>
                            <Stack {...orderSummaryTheme.savingsBreakdownStack}>
                                {breakdownLines.map((line) => (
                                    <Flex key={line.key} {...orderSummaryTheme.savingsBreakdownRow}>
                                        <Stack {...orderSummaryTheme.savingsBreakdownTextStack}>
                                            <Text {...orderSummaryTheme.savingsBreakdownRowText}>
                                                {renderSavingsLineLabel(line.lineType)}
                                            </Text>
                                            {/* data-private: a loyalty line reads "X points applied" and a coupon
                                                line may show the coupon code/promo — PII for LogRocket */}
                                            <Text
                                                {...orderSummaryTheme.savingsBreakdownDetail}
                                                {...(['loyalty', 'coupon'].includes(line.lineType)
                                                    ? {'data-private': true}
                                                    : {})}
                                            >
                                                {line.detail}
                                            </Text>
                                            {line.lineType === 'coupon' && line.codeUsed && (
                                                // data-private: coupon code is redacted from LogRocket replays
                                                <Text
                                                    {...orderSummaryTheme.savingsBreakdownRowText}
                                                    data-private
                                                >
                                                    {line.codeUsed}
                                                </Text>
                                            )}
                                        </Stack>
                                        <Text {...orderSummaryTheme.rowTextSavings}>
                                            <FormattedNumber
                                                style="currency"
                                                currency={basket?.currency}
                                                value={-toPositiveAmount(line.amount)}
                                            />
                                        </Text>
                                    </Flex>
                                ))}
                            </Stack>
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

SavingsAccordion.propTypes = {
    basket: PropTypes.object,
    productsDiscountTotal: PropTypes.number
}

export default SavingsAccordion
